import React, { useState } from 'react';
import { useQuery, useQueryClient } from 'react-query';
import toast from 'react-hot-toast';
import {
  ClipboardDocumentIcon,
  PlusIcon,
  ArrowPathIcon,
  NoSymbolIcon,
  CheckIcon,
} from '@heroicons/react/24/outline';
import { apiClient as sharedApiClient } from '../../services/api';
import { useOperationsAPI } from '../../context/useOperationsAPI';
import PageLayout from '../../components/PageLayout';
import { AdminModalShell } from '../../components/AdminSurface';
import { useAuth } from '../../context/AuthContext';

type Tool = { name: string; description: string; write: boolean };
type Credential = {
  id: string;
  name: string;
  prefix: string;
  company_ids: string[];
  tools: string[];
  expires_at: string;
  revoked_at: string | null;
  last_used_at: string | null;
};
type Configuration = {
  endpoint: string;
  companies: { id: string; name: string }[];
  tools: Tool[];
  credentials: Credential[];
};
type Action = {
  id: string;
  tool: string;
  status: string;
  company_id: string;
  preview: Record<string, any>;
  result: any;
  error: string | null;
  created_at: string;
  expires_at: string;
};
const label = (value: string) =>
  value.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()) +
  (['unit_amount', 'discount_amount'].includes(value) ? ' (cents)' : '');
const toggle = (values: string[], value: string) =>
  values.includes(value)
    ? values.filter((x) => x !== value)
    : [...values, value];
const date = (value?: string | null) =>
  value ? new Date(value).toLocaleString() : 'Never';

function RecordView({
  value,
  compact = false,
}: {
  value: any;
  compact?: boolean;
}) {
  if (value === null || value === undefined)
    return <span className="text-on-surface-variant">None</span>;
  if (Array.isArray(value))
    return (
      <ol className="divide-y divide-outline/40">
        {value.map((v, i) => (
          <li className="py-2" key={i}>
            <RecordView value={v} compact={compact} />
          </li>
        ))}
      </ol>
    );
  if (typeof value === 'object')
    return (
      <dl className="space-y-2">
        {Object.entries(value).map(([key, v]) => (
          <div
            key={key}
            className={`grid min-w-0 gap-1 ${
              compact ? '' : 'sm:grid-cols-[160px_minmax(0,1fr)]'
            }`}
          >
            <dt className="break-words text-sm font-medium text-on-surface-variant">
              {label(key)}
            </dt>
            <dd className="min-w-0 break-words text-sm">
              <RecordView value={v} compact />
            </dd>
          </div>
        ))}
      </dl>
    );
  return (
    <span className="whitespace-pre-wrap break-words">
      {typeof value === 'boolean' ? (value ? 'Yes' : 'No') : String(value)}
    </span>
  );
}

export function InvoiceLinkReview({
  rows,
  result,
}: {
  rows: any[];
  result?: any;
}) {
  const [search, setSearch] = useState('');
  const visible = rows.filter((row) =>
    JSON.stringify(row).toLowerCase().includes(search.toLowerCase())
  );
  return (
    <section
      className="min-w-0 space-y-3"
      aria-label="Invoice reservation corrections"
    >
      <input
        aria-label="Search invoice corrections"
        className="w-full rounded-md border border-outline bg-surface p-2"
        value={search}
        onChange={(event) => setSearch(event.target.value)}
        placeholder="Search invoice or reservation"
      />
      <div className="max-w-full overflow-x-auto">
        <table className="w-full table-fixed text-left text-sm">
          <thead className="hidden md:table-header-group">
            <tr className="border-b border-outline/40">
              {[
                'Invoice',
                'Previous reservation',
                'New reservation',
                'Decision / result',
              ].map((name) => (
                <th key={name} scope="col" className="p-2">
                  {name}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {visible.map((row) => {
              const outcome = result?.rows?.find(
                (item: any) => item.invoice_id === row.invoice_id
              );
              const { fingerprint: _fingerprint, ...details } = row;
              return (
                <tr
                  key={row.invoice_id}
                  className="block border-b border-outline/40 align-top md:table-row"
                >
                  <td className="block break-words p-2 md:table-cell">
                    <span className="block font-semibold md:hidden">
                      Invoice
                    </span>
                    {row.invoice_number || row.invoice_id}
                    <details className="mt-2">
                      <summary className="cursor-pointer">Details</summary>
                      <RecordView value={details} compact />
                    </details>
                  </td>
                  <td className="block break-words p-2 md:table-cell">
                    <span className="block font-semibold md:hidden">
                      Previous reservation
                    </span>
                    {row.previous_reservation_id || 'None'}
                  </td>
                  <td className="block break-words p-2 md:table-cell">
                    <span className="block font-semibold md:hidden">
                      New reservation
                    </span>
                    {row.reservation_ref || row.reservation_id || 'None'}
                  </td>
                  <td className="block break-words p-2 md:table-cell">
                    <span className="block font-semibold md:hidden">
                      Decision / result
                    </span>
                    {label(row.decision)}
                    <p className="mt-1">{row.reason}</p>
                    {outcome && (
                      <p className="mt-2 font-semibold">
                        {label(outcome.status)}
                      </p>
                    )}
                    {outcome?.error && (
                      <p className="mt-1 text-red-600">{outcome.error}</p>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <p className="text-sm text-on-surface-variant">
        {visible.length} of {rows.length} corrections
      </p>
    </section>
  );
}

export function IntakeReview({ batch, result }: { batch: any; result?: any }) {
  const [search, setSearch] = useState('');
  const [decision, setDecision] = useState('all');
  const rows = (batch.rows || []).filter(
    (r: any) =>
      (decision === 'all' || r.decision === decision) &&
      `${r.key} ${r.operation} ${r.error || ''}`
        .toLowerCase()
        .includes(search.toLowerCase())
  );
  return (
    <div className="min-w-0 space-y-4">
      <dl className="flex flex-wrap gap-x-6 gap-y-2 text-sm">
        {Object.entries(batch.counts || {}).map(([key, value]) => (
          <div key={key}>
            <dt>{label(key)}</dt>
            <dd className="font-semibold">{String(value)}</dd>
          </div>
        ))}
        <div>
          <dt>Draft invoice value</dt>
          <dd className="font-semibold">
            {new Intl.NumberFormat('en-US', {
              style: 'currency',
              currency: 'USD',
            }).format((batch.invoice_total_cents || 0) / 100)}
          </dd>
        </div>
      </dl>
      {batch.blocked && (
        <p role="alert" className="text-sm text-red-600">
          Conflicts must be resolved before approval.
        </p>
      )}
      <div className="flex flex-wrap gap-3">
        <input
          aria-label="Search batch rows"
          placeholder="Search source keys"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="min-w-0 flex-1 rounded-md border border-outline bg-surface p-2"
        />
        <select
          aria-label="Row decision"
          value={decision}
          onChange={(e) => setDecision(e.target.value)}
          className="rounded-md border border-outline bg-surface p-2"
        >
          {['all', 'create', 'match', 'resume', 'completed', 'conflict'].map(
            (v) => (
              <option key={v} value={v}>
                {label(v)}
              </option>
            )
          )}
        </select>
      </div>
      <div className="max-h-[55vh] overflow-auto border-y border-outline/40">
        <table className="w-full min-w-[600px] text-left text-sm">
          <thead className="sticky top-0 bg-surface">
            <tr>
              {['Source key', 'Operation', 'Decision', 'Result', 'Review'].map(
                (h) => (
                  <th key={h} className="p-3">
                    {h}
                  </th>
                )
              )}
            </tr>
          </thead>
          <tbody>
            {rows.map((row: any) => {
              const outcome = result?.rows?.find((r: any) => r.key === row.key);
              return (
                <tr
                  key={row.key}
                  className="border-t border-outline/40 align-top"
                >
                  <td className="max-w-[180px] break-words p-3">{row.key}</td>
                  <td className="p-3">{label(row.operation)}</td>
                  <td className="p-3">{label(row.decision)}</td>
                  <td className="p-3">
                    {outcome ? label(outcome.status) : 'Not run'}
                  </td>
                  <td className="max-w-[480px] p-3">
                    <details>
                      <summary className="cursor-pointer">Details</summary>
                      {(row.error || outcome?.error) && (
                        <p className="my-2 text-red-600">
                          {row.error || outcome.error}
                        </p>
                      )}
                      <RecordView
                        compact
                        value={{
                          requested: row.input,
                          current: row.current,
                          references_and_terms: row.references,
                          result: outcome || null,
                        }}
                      />
                    </details>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <p className="text-sm text-on-surface-variant">
        {rows.length} of {batch.rows?.length || 0} rows
      </p>
    </div>
  );
}

export default function AiAccess() {
  const { user, operationsScope } = useAuth();
  const reviewScope = JSON.stringify([
    window.location.host, user?.id, user?.role, user?.is_platform_admin,
    user?.is_support_staff, user?.accessible_company_ids,
    user?.support_write_permissions,
    operationsScope,
  ]);
  return user ? (
    <AiAccessWorkspace key={reviewScope} reviewScope={reviewScope} />
  ) : (
    <PageLayout><p role="alert">Sign in to review your AI activity.</p></PageLayout>
  );
}

function AiAccessWorkspace({ reviewScope }: { reviewScope: string }) {
  const apiClient = useOperationsAPI(sharedApiClient);
  const client = useQueryClient();
  const config = useQuery<Configuration>(
    ['ai-access', reviewScope],
    async () => (await apiClient.get('/ai-access')).data,
    { retry: false, cacheTime: 0 }
  );
  const history = useQuery<Action[]>(
    ['ai-actions', reviewScope],
    async () => (await apiClient.get('/ai-access/actions')).data,
    { enabled: Boolean(config.data), refetchInterval: 5000, cacheTime: 0 }
  );
  const [tab, setTab] = useState<'connections' | 'actions'>('connections');
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState('');
  const [days, setDays] = useState(30);
  const [tenants, setTenants] = useState<string[]>([]);
  const [tools, setTools] = useState<string[]>([]);
  const [token, setToken] = useState('');
  const [selected, setSelected] = useState<string | null>(null);
  const [revoke, setRevoke] = useState<Credential | null>(null);
  const [busy, setBusy] = useState(false);
  const [acknowledged, setAcknowledged] = useState(false);
  const action = history.data?.find((a) => a.id === selected);
  const pending =
    history.data?.filter((a) => a.status === 'pending_review').length || 0;
  const refresh = async () => {
    await client.invalidateQueries(['ai-access', reviewScope]);
    await client.invalidateQueries(['ai-actions', reviewScope]);
  };
  const perform = async (work: () => Promise<unknown>) => {
    setBusy(true);
    try {
      await work();
      await refresh();
    } catch (e: any) {
      toast.error(
        e?.response?.data?.message ||
          'Operation failed. Refresh and check its status.'
      );
    } finally {
      setBusy(false);
    }
  };
  const copy = async (value: string) => {
    try {
      await navigator.clipboard.writeText(value);
      toast.success('Copied');
    } catch {
      toast.error('Clipboard unavailable');
    }
  };
  const create = () =>
    perform(async () => {
      const result = await apiClient.post('/ai-access/credentials', {
        name,
        company_ids: tenants,
        tools,
        expires_in_days: days,
      });
      setToken(result.data.token);
      setCreating(false);
      setName('');
      setTenants([]);
      setTools([]);
    });
  const button =
    'inline-flex min-h-[40px] items-center justify-center gap-2 rounded-md border border-outline px-3 py-2 text-sm font-medium disabled:opacity-40';

  return (
    <PageLayout>
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-2xl font-bold">AI Access</h1>
        <div className="flex gap-2">
          <button
            className={button}
            title="Refresh"
            aria-label="Refresh"
            onClick={() => void refresh()}
          >
            <ArrowPathIcon className="h-5 w-5" />
          </button>
          <button
            className={`${button} bg-primary text-on-primary`}
            disabled={!config.data}
            onClick={() => setCreating(true)}
          >
            <PlusIcon className="h-5 w-5" />
            New connection
          </button>
        </div>
      </div>
      {config.isLoading && <p role="status">Loading connections...</p>}
      {config.isError && (
        <p role="alert" className="text-red-600">
          {(config.error as any)?.response?.status === 404
            ? 'AI Access is not enabled in this environment.'
            : 'Unable to load AI Access. Check your administrator access and try again.'}
        </p>
      )}
      {config.data && (
        <>
          <div className="mb-5 flex min-w-0 items-center gap-3 border-b border-outline/40 pb-5">
            <span className="text-sm font-medium">MCP endpoint</span>
            <code className="min-w-0 flex-1 break-all text-sm">
              {config.data.endpoint}
            </code>
            <button
              className={button}
              title="Copy endpoint"
              aria-label="Copy endpoint"
              onClick={() => void copy(config.data!.endpoint)}
            >
              <ClipboardDocumentIcon className="h-5 w-5" />
            </button>
          </div>
          <div
            role="tablist"
            aria-label="AI access views"
            className="mb-5 flex gap-5 border-b border-outline/40"
          >
            {(['connections', 'actions'] as const).map((t) => (
              <button
                role="tab"
                aria-selected={tab === t}
                className={`border-b-2 px-1 py-3 text-sm font-semibold ${
                  tab === t
                    ? 'border-primary text-primary'
                    : 'border-transparent'
                }`}
                key={t}
                onClick={() => setTab(t)}
              >
                {t === 'actions'
                  ? `Activity${pending ? ` (${pending} to review)` : ''}`
                  : 'Connections'}
              </button>
            ))}
          </div>
          <div className="overflow-x-auto">
            {tab === 'connections' ? (
              <table className="w-full min-w-[650px] text-left text-sm">
                <thead>
                  <tr className="border-b border-outline/50">
                    {[
                      'Connection',
                      'Tenants',
                      'Expires',
                      'Last used',
                      'Status',
                      '',
                    ].map((h, i) => (
                      <th key={i} className="px-3 py-3">
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {config.data.credentials.map((c) => (
                    <tr className="border-b border-outline/30" key={c.id}>
                      <td className="px-3 py-4">
                        <div className="font-semibold">{c.name}</div>
                        <code className="text-xs">{c.prefix}...</code>
                      </td>
                      <td className="max-w-xs px-3 py-4">
                        {c.company_ids
                          .map(
                            (id) =>
                              config.data!.companies.find((t) => t.id === id)
                                ?.name || 'Unavailable tenant'
                          )
                          .join(', ')}
                      </td>
                      <td className="px-3 py-4">{date(c.expires_at)}</td>
                      <td className="px-3 py-4">{date(c.last_used_at)}</td>
                      <td className="px-3 py-4">
                        {c.revoked_at
                          ? 'Revoked'
                          : new Date(c.expires_at) < new Date()
                          ? 'Expired'
                          : 'Active'}
                      </td>
                      <td className="px-3 py-4">
                        <button
                          disabled={Boolean(c.revoked_at)}
                          className={button}
                          title={`Revoke ${c.name}`}
                          aria-label={`Revoke ${c.name}`}
                          onClick={() => setRevoke(c)}
                        >
                          <NoSymbolIcon className="h-5 w-5" />
                        </button>
                      </td>
                    </tr>
                  ))}
                  {!config.data.credentials.length && (
                    <tr>
                      <td
                        colSpan={6}
                        className="py-10 text-center text-on-surface-variant"
                      >
                        No connections
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            ) : (
              <table className="w-full min-w-[620px] text-left text-sm">
                <thead>
                  <tr className="border-b border-outline/50">
                    {['Operation', 'Tenant', 'Requested', 'Status', ''].map(
                      (h, i) => (
                        <th key={i} className="px-3 py-3">
                          {h}
                        </th>
                      )
                    )}
                  </tr>
                </thead>
                <tbody>
                  {history.data?.map((a) => (
                    <tr className="border-b border-outline/30" key={a.id}>
                      <td className="px-3 py-4 font-medium">{label(a.tool)}</td>
                      <td className="px-3 py-4">{a.preview?.tenant?.name}</td>
                      <td className="px-3 py-4">{date(a.created_at)}</td>
                      <td className="px-3 py-4">{label(a.status)}</td>
                      <td className="px-3 py-4">
                        <button
                          className={button}
                          onClick={() => {
                            setSelected(a.id);
                            setAcknowledged(false);
                          }}
                        >
                          {a.status === 'pending_review' ? 'Review' : 'Details'}
                        </button>
                      </td>
                    </tr>
                  ))}
                  {!history.data?.length && (
                    <tr>
                      <td
                        colSpan={5}
                        className="py-10 text-center text-on-surface-variant"
                      >
                        No activity
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            )}
          </div>
          {history.isError && (
            <p role="alert" className="mt-3 text-red-600">
              Activity could not refresh. Do not repeat an operation until its
              status is confirmed.
            </p>
          )}
        </>
      )}
      {creating && config.data && (
        <AdminModalShell
          title="New AI connection"
          onClose={() => !busy && setCreating(false)}
          footer={
            <button
              disabled={
                busy || !name.trim() || !tenants.length || !tools.length
              }
              className={`${button} bg-primary text-on-primary`}
              onClick={create}
            >
              Create connection
            </button>
          }
        >
          <div className="space-y-5">
            <label className="block text-sm font-medium">
              Connection name
              <input
                className="mt-1 w-full rounded-md border border-outline bg-surface p-2"
                value={name}
                maxLength={100}
                onChange={(e) => setName(e.target.value)}
                placeholder="Brian - Claude"
              />
            </label>
            <label className="block text-sm font-medium">
              Expires in days
              <input
                type="number"
                min={1}
                max={90}
                className="ml-3 w-20 rounded-md border border-outline bg-surface p-2"
                value={days}
                onChange={(e) => setDays(Number(e.target.value))}
              />
            </label>
            <fieldset>
              <legend className="mb-2 font-semibold">Tenants</legend>
              <div className="max-h-48 space-y-2 overflow-y-auto">
                {config.data.companies.map((c) => (
                  <label className="flex items-center gap-2 text-sm" key={c.id}>
                    <input
                      type="checkbox"
                      checked={tenants.includes(c.id)}
                      onChange={() => setTenants(toggle(tenants, c.id))}
                    />
                    {c.name}
                  </label>
                ))}
              </div>
            </fieldset>
            <fieldset>
              <legend className="mb-2 font-semibold">Operations</legend>
              <div className="grid gap-x-5 gap-y-2 sm:grid-cols-2">
                {config.data.tools
                  .filter(
                    (t) => !['tenants_list', 'actions_get'].includes(t.name)
                  )
                  .map((t) => (
                    <label
                      className="flex items-start gap-2 text-sm"
                      title={t.description}
                      key={t.name}
                    >
                      <input
                        className="mt-1"
                        type="checkbox"
                        checked={tools.includes(t.name)}
                        onChange={() => setTools(toggle(tools, t.name))}
                      />
                      <span>
                        {label(t.name)}
                        <span className="block text-xs text-on-surface-variant">
                          {t.write ? 'Requires review' : 'Read only'}
                        </span>
                      </span>
                    </label>
                  ))}
              </div>
            </fieldset>
          </div>
        </AdminModalShell>
      )}
      {token && (
        <AdminModalShell
          title="Connection credential"
          onClose={() => setToken('')}
          footer={
            <button className={button} onClick={() => setToken('')}>
              Done
            </button>
          }
        >
          <p className="mb-4 text-sm text-amber-700">
            Shown once. Keep this credential private. Never paste it into a
            conversation or share it with another person.
          </p>
          <div className="flex min-w-0 items-start gap-2">
            <code className="min-w-0 flex-1 break-all rounded-md bg-surface-variant p-3 text-sm">
              {token}
            </code>
            <button
              className={button}
              title="Copy credential"
              aria-label="Copy credential"
              onClick={() => void copy(token)}
            >
              <ClipboardDocumentIcon className="h-5 w-5" />
            </button>
          </div>
        </AdminModalShell>
      )}
      {revoke && (
        <AdminModalShell
          title={`Revoke ${revoke.name}?`}
          onClose={() => !busy && setRevoke(null)}
          footer={
            <button
              disabled={busy}
              className={`${button} text-red-600`}
              onClick={() =>
                void perform(async () => {
                  await apiClient.post(
                    `/ai-access/credentials/${revoke.id}/revoke`
                  );
                  setRevoke(null);
                })
              }
            >
              Revoke connection
            </button>
          }
        >
          <p>
            Pending actions will be cancelled. Work already started may still
            complete.
          </p>
        </AdminModalShell>
      )}
      {action && (
        <AdminModalShell
          title={label(action.tool)}
          onClose={() => !busy && setSelected(null)}
          maxWidthClassName="max-w-4xl"
          footer={
            <div className="flex flex-wrap items-center justify-end gap-3">
              {['pending_review', 'approved'].includes(action.status) && (
                <button
                  className={button}
                  disabled={busy}
                  onClick={() =>
                    void perform(async () => {
                      await apiClient.post(
                        `/ai-access/actions/${action.id}/cancel`
                      );
                      setSelected(null);
                    })
                  }
                >
                  Cancel action
                </button>
              )}
              {action.status === 'pending_review' && (
                <button
                  className={`${button} bg-primary text-on-primary`}
                  disabled={
                    busy ||
                    !acknowledged ||
                    Boolean(action.preview?.batch?.blocked) ||
                    new Date(action.expires_at) <= new Date()
                  }
                  onClick={() =>
                    void perform(async () => {
                      await apiClient.post(
                        `/ai-access/actions/${action.id}/approve`
                      );
                      setSelected(null);
                      toast.success('Approved for execution');
                    })
                  }
                >
                  <CheckIcon className="h-5 w-5" />
                  Approve and run
                </button>
              )}
            </div>
          }
        >
          <dl className="mb-5 grid grid-cols-2 gap-4 text-sm">
            <div>
              <dt className="text-on-surface-variant">Tenant</dt>
              <dd className="font-semibold">{action.preview?.tenant?.name}</dd>
            </div>
            <div>
              <dt className="text-on-surface-variant">Status</dt>
              <dd>{label(action.status)}</dd>
            </div>
            <div>
              <dt className="text-on-surface-variant">Review expires</dt>
              <dd>{date(action.expires_at)}</dd>
            </div>
            <div>
              <dt className="text-on-surface-variant">Customer email</dt>
              <dd>
                {action.preview?.customer_email_will_be_sent
                  ? 'Will be sent'
                  : 'Will not be sent'}
              </dd>
            </div>
          </dl>
          {action.preview?.warning && (
            <p className="mb-5 text-sm text-amber-700">
              {action.preview.warning}
            </p>
          )}
          <h3 className="mb-3 font-semibold">Requested changes</h3>
          {action.tool.startsWith('invoices_link_') ? (
            <InvoiceLinkReview
              key={action.id}
              rows={action.preview?.current?.rows || []}
              result={action.result}
            />
          ) : action.preview?.batch ? (
            <IntakeReview
              key={action.id}
              batch={action.preview.batch}
              result={action.result}
            />
          ) : (
            <RecordView value={action.preview?.input} />
          )}
          {action.preview?.current &&
            !action.tool.startsWith('invoices_link_') && (
              <details className="mt-5 border-t border-outline/40 pt-4">
                <summary className="cursor-pointer font-semibold">
                  Current records and terms
                </summary>
                <div className="mt-3">
                  <RecordView value={action.preview.current} />
                </div>
              </details>
            )}
          {action.result &&
            !action.preview?.batch &&
            !action.tool.startsWith('invoices_link_') && (
              <section className="mt-5 border-t border-outline/40 pt-4">
                <h3 className="mb-3 font-semibold">Result</h3>
                <RecordView value={action.result} />
              </section>
            )}
          {action.error && (
            <p role="alert" className="mt-4 text-sm text-red-600">
              {action.error}
            </p>
          )}
          {action.status === 'pending_review' && (
            <label className="mt-6 flex items-start gap-2 border-t border-outline/40 pt-4 text-sm">
              <input
                className="mt-1"
                type="checkbox"
                checked={acknowledged}
                onChange={(e) => setAcknowledged(e.target.checked)}
              />
              I approve these changes for {action.preview?.tenant?.name}
              {action.preview?.customer_email_will_be_sent
                ? ', including the customer email'
                : ''}
              .
            </label>
          )}
        </AdminModalShell>
      )}
    </PageLayout>
  );
}
